import { provideHttpClient, withInterceptors } from "@angular/common/http";
import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    isDevMode,
    provideAppInitializer
} from "@angular/core";
import { LuxonDateAdapter } from "@angular/material-luxon-adapter";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withInMemoryScrolling } from "@angular/router";
import { provideFuse } from "@fuse";
import { TranslocoService, provideTransloco } from "@jsverse/transloco";
import { appRoutes } from "@app/app.routes";
import { provideIcons } from "@app/core/icons/icons.provider";
import { firstValueFrom } from "rxjs";
import { authInterceptor } from "./core/auth/auth.interceptor";
import { fuseLoadingInterceptor } from "@fuse/services/loading/loading.interceptor";
import { AuthService } from "./core/auth/auth.service";
import { GlobalErrorHandler } from "./core/services/global-error-handler/global-error-handler";
import { TranslocoHttpLoader } from "./core/transloco/transloco.http-loader";

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(withInterceptors([fuseLoadingInterceptor, authInterceptor])),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: "top" })
        ),
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        // Material Date Adapter
        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: "D"
                },
                display: {
                    dateInput: "DDD",
                    monthYearLabel: "LLL yyyy",
                    dateA11yLabel: "DD",
                    monthYearA11yLabel: "LLLL yyyy"
                }
            }
        },

        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: "en",
                        label: "English"
                    },
                    {
                        id: "it",
                        label: "Italian"
                    }
                ],
                defaultLang: "en",
                fallbackLang: "en",
                reRenderOnLangChange: true,
                prodMode: !isDevMode()
            },
            loader: TranslocoHttpLoader
        }),
        provideAppInitializer(() => {
            const translocoService = inject(TranslocoService);
            translocoService.setActiveLang("it");
            return firstValueFrom(translocoService.load("it"));
        }),
        provideAppInitializer(() => {
            const authService = inject(AuthService);
            return firstValueFrom(authService.init());
        }),
        // Fuse
        provideIcons(),
        provideFuse({
            fuse: {
                layout: "classic",
                scheme: "light",
                screens: {
                    sm: "600px",
                    md: "960px",
                    lg: "1280px",
                    xl: "1440px"
                },
                theme: "theme-default",
                themes: [
                    {
                        id: "theme-default",
                        name: "Default"
                    },
                    {
                        id: "theme-brand",
                        name: "Brand"
                    },
                    {
                        id: "theme-teal",
                        name: "Teal"
                    },
                    {
                        id: "theme-rose",
                        name: "Rose"
                    },
                    {
                        id: "theme-purple",
                        name: "Purple"
                    },
                    {
                        id: "theme-amber",
                        name: "Amber"
                    }
                ]
            }
        })
    ]
};
